import {useAuthService} from "~/composables/useAuthService";
import {useMessaging} from "~/composables/useMessaging";
import {computed} from "vue";
import {useRouter, useRoute} from "vue-router";
import {useMailer} from "~/composables/useMailer";
import {canSwitchClub} from "~/composables/useAdminRouteGuard";
import {signOutToHome} from "~/composables/useSignOut";
import {EVALS_ENABLED_STATE} from "~/composables/useAssistantEvals";
import {useMembershipsSetup} from "~/composables/useMembershipsAdmin";
import {useBookingsSetup} from "~/composables/useBookingsAdmin";
import {onSet} from "~/utils/adminBreadcrumbTrail";

export function useNavigationItems() {
  const {
    isAuthenticated,
    user,
    logout,
    stopImpersonating,
    isImpersonating,
    isSuperUser
  } = useAuthService();

  const { unreadCount } = useMessaging();
  // "Set up" beside Memberships & passes until a type is Live (D30); the
  // layout loads this when the club has memberships switched on.
  const {needsSetup: membershipsNeedSetup, pendingApplications: membershipApplicationsPending} = useMembershipsSetup();
  // Playing areas › Bookings shows "Set up" until bookings are switched on
  // (BOOKINGS_PLAN.md §5); the sub-item is only there once Tap Leagues has
  // enabled the feature for the club (B1).
  // Shown unless the settings say the feature is OFF: the settings load after
  // the first render, and a sub-item that pops in late reads as a glitch.
  const {bookingsSetup, needsSetup: bookingsNeedSetup} = useBookingsSetup();
  const bookingsFeature = computed(() => bookingsSetup.value.featureEnabled !== false);
  const {stringifyQuery} = useCrudConverters();
  const router = useRouter();
  const route = useRoute();

  const adminBreadcrumbs = useState('adminBreadcrumbs', () => []);
  // X165 (R76 S1): true from a path change under /admin until the new page sets its trail (utils/adminBreadcrumbTrail)
  const adminBreadcrumbsPending = useState('adminBreadcrumbsPending', () => false);
  // Filled once per session by the admin layout (useAssistantEvalsNav): true only when the
  // API's eval status says enabled, so the Evals item never points at a switched off console.
  const assistantEvalsEnabled = useState(EVALS_ENABLED_STATE, () => null);

  const convertToLocalRoute = (link) => {
    const config = useRuntimeConfig();
    const baseUrl = config.public.baseUrl;
    if(link.startsWith(baseUrl)) {
      return link.replace(baseUrl, '');
    }
    return null;
  }

  const setAdminBreadcrumbs = (items) => {
    const next = onSet({crumbs: adminBreadcrumbs.value, pending: adminBreadcrumbsPending.value}, items);
    adminBreadcrumbs.value = next.crumbs;
    adminBreadcrumbsPending.value = next.pending;
  };

  const superUserItems = computed(() => {
    const items = [];
    items.push({
      icon: 'mdi-view-dashboard',
      title: 'Dashboard',
      link: `/admin/super`,
      active: false
    });
    items.push({
      icon: 'mdi-account',
      title: 'Users',
      link: `/admin/super/users`,
      active: route.path.includes('/admin/super/users')
    });
    items.push({
      icon: 'mdi-map-marker-path',
      title: 'Organiser journey',
      link: `/admin/super/journey`,
      active: route.path.includes('/admin/super/journey')
    });
    items.push({
      icon: 'mdi-email-edit-outline',
      title: 'Outreach',
      link: `/admin/super/outreach`,
      active: route.path.includes('/admin/super/outreach')
    });
    items.push({
      icon: 'mdi-access-point',
      title: 'Online users',
      link: `/admin/super/online-users`,
      active: route.path.includes('/admin/super/online-users')
    });
    items.push({
      icon: 'mdi-account-multiple',
      title: 'Players',
      link: `/admin/super/players`,
      active: route.path.includes('/admin/super/players')
    });
    items.push({
      icon: 'mdi-account-switch',
      title: 'Player Merges',
      link: `/admin/super/player-merges`,
      active: route.path.includes('/admin/super/player-merges')
    });
    items.push({
      icon: 'mdi-account-convert',
      title: 'Account Merges',
      link: `/admin/super/account-merges`,
      active: route.path.includes('/admin/super/account-merges')
    });
    items.push({
      icon: 'mdi-account-group',
      title: 'Clubs',
      link: `/admin/super/clubs`,
      active: route.path.includes('/admin/super/clubs')
    });
    items.push({
      icon: 'mdi-email-outline',
      title: 'Emails',
      appendIcon: "mdi-new-box",
      appendIconColor: "primary",
      link: `/admin/super/emails`,
      active: route.path.includes('/admin/super/emails')
    });
    items.push({
      icon: 'mdi-credit-card-outline',
      title: 'Payments',
      link: null,
      active: route.path.includes('/admin/super/payments')
          || route.path.includes('/admin/super/payment-accounts')
          || route.path.includes('/admin/super/commission')
          || route.path.includes('/admin/super/countries-commission')
          || route.path.includes('/admin/super/vat'),
      subItems: [
        {
          title: 'Overview',
          link: '/admin/super/payments',
          icon: 'mdi-cash-multiple'
        },
        {
          title: 'Connected accounts',
          link: '/admin/super/payment-accounts',
          icon: 'mdi-bank-outline'
        },
        {
          title: 'Countries and commission',
          link: '/admin/super/countries-commission',
          icon: 'mdi-percent-outline'
        },
        {
          title: 'VAT',
          link: '/admin/super/vat',
          icon: 'mdi-receipt-text-outline'
        }
      ]
    });
    // The platform Terms of Use and Privacy Policy. Distinct from Terms
    // Template below, which is the sample terms seeded into new paid meets.
    items.push({
      icon: 'mdi-scale-balance',
      title: 'Terms and privacy',
      link: `/admin/super/legal`,
      active: route.path.includes('/admin/super/legal')
    });
    items.push({
      icon: 'mdi-gavel',
      title: 'Terms Template',
      link: `/admin/super/terms-template`,
      active: route.path.includes('/admin/super/terms-template')
    });
    // Membership terms and health questionnaires clubs follow (D25).
    items.push({
      icon: 'mdi-card-account-details-outline',
      title: 'Membership templates',
      link: `/admin/super/templates`,
      active: route.path.includes('/admin/super/templates')
    });
    items.push({
      icon: 'mdi-help-box',
      title: 'Help',
      link: null,
      active: route.path.includes('/admin/super/help'),
      subItems: [
        {
          title: 'Categories',
          link: '/admin/super/help-categories',
          icon: 'mdi-folder-multiple'
        },
        {
          title: 'Articles',
          link: '/admin/super/help-articles',
          icon: 'mdi-help-circle'
        }
      ]
    });
    // The setup assistant (SETUP_ASSISTANT_PLAN.md S7) has its own page under Assistant;
    // the eval console sits there too, only where it is switched on.
    items.push({
      // Grant 2026-11-02: the help chat is Tappy; the chat-question bubble is its icon everywhere.
      icon: 'mdi-chat-question-outline',
      title: 'Tappy',
      link: null,
      active: route.path.includes('/admin/super/assistant') || route.path.includes('/admin/super/setup-assistant'),
      subItems: [
        {title: 'Overview', link: '/admin/super/assistant', icon: 'mdi-chart-box-outline'},
        {title: 'Setup assistant', link: '/admin/super/setup-assistant', icon: 'mdi-creation-outline'},
        ...(assistantEvalsEnabled.value === true ? [{title: 'Evals', link: '/admin/super/assistant/evals', icon: 'mdi-clipboard-check-outline'}] : []),
      ]
    });
    items.push({
      icon: 'mdi-flag-outline',
      title: 'Message reports',
      link: `/admin/super/reports`,
      active: route.path.includes('/admin/super/reports')
    });
    items.push({
      icon: 'mdi-form-select',
      title: 'Prompts',
      link: `/admin/super/prompts`,
      active: route.path.includes('/admin/super/prompts')
    });
    items.push({
      icon: 'mdi-basketball',
      title: 'Sports',
      link: `/admin/super/sports`,
      active: route.path.includes('/admin/super/sports')
    });
    items.push({
      icon: 'mdi-link-variant',
      title: 'Short Links',
      link: `/admin/super/short-links`,
      active: route.path.includes('/admin/super/short-links')
    });
    items.push({
      icon: 'mdi-star-box',
      title: 'Testimonials',
      link: `/admin/super/testimonials`,
      active: route.path.includes('/admin/super/testimonials')
    });
    items.push({
      icon: 'mdi-send',
      title: 'Mass Mailer',
      link: `/admin/super/mailer`,
      active: route.path.includes('/admin/super/mailer')
    });
    items.push({
      icon: 'mdi-bell-ring',
      title: 'Push Notifications',
      link: `/admin/super/push`,
      active: route.path.includes('/admin/super/push')
    });
    items.push({
      icon: 'mdi-bullhorn',
      title: 'Announcements',
      link: `/admin/super/announcements`,
      active: route.path.includes('/admin/super/announcements')
    });
    items.push({
      icon: 'mdi-cellphone-arrow-down',
      title: 'Supported Versions',
      link: `/admin/super/supported-versions`,
      active: route.path.includes('/admin/super/supported-versions')
    });
    items.push({
      icon: 'mdi-star-outline',
      title: 'Review Prompt',
      link: `/admin/super/review-prompt`,
      active: route.path.includes('/admin/super/review-prompt')
    });
    items.push({
      icon: 'mdi-shield-check-outline',
      title: 'Two-step sign-in',
      link: `/admin/super/two-step`,
      active: route.path.includes('/admin/super/two-step')
    });
    return items;
  });
 const { filtersToQuery } = useMailer();
  const adminMenuItems = computed(() => {
    const items = [];
    if (isAuthenticated.value) {
      if (route.params.clubId
      ) {
        const clubId = route.params.clubId;
        const under = (tail) => route.path.includes('/admin/clubs/' + clubId + tail);
        // X142 (Grant 2026-10-26): the menu is grouped under collapsible section rows (utils/adminMenu.js: Your club ·
        // People · Play · Outreach · Requests & reports); each item names its section and is drawn as a
        // sub-item of it. The layout drops Payments / Memberships & passes while the club's feature is off, and a
        // section with nothing left in it.

        // ── Your club ──
        // X103 G3: the club's own details, logo, who can see it, joining and deletion (the dashboard's
        // Club setup tiles used to be the only way in). The cog is settings everywhere in the admin.
        // X108 (Grant): first, above Players.
        items.push({
          icon: 'mdi-cog-outline',
          title: 'Club settings',
          link: `/admin/clubs/${route.params.clubId}/settings`,
          active: under('/settings'),
          section: 'club',
        });
        // Playing areas and Bookings as two plain rows (X142; they were a dropdown). Setup is the list page.
        items.push({
          icon: 'mdi-map-marker-outline',
          title: 'Playing areas',
          link: `/admin/clubs/${route.params.clubId}/playingAreas`,
          exact: true,
          active: under('/playingAreas') && !under('/playingAreas/bookings'),
          section: 'club',
        });
        // R16-L8: always in the list, flagged hidden when the feature is off. The layout's drawer renders
        // during SSR before the Playing areas pages have loaded the settings (feature unknown, so shown),
        // and the client hydrated with the loaded "off": one item fewer, a hydration mismatch that also
        // dropped later drawer items. The drawer drops hidden ones only after mount (AdminLayout navMounted).
        // "Set up" until bookings are switched on (BOOKINGS_PLAN.md §5).
        items.push({
          icon: 'mdi-calendar-clock-outline',
          title: 'Bookings',
          link: `/admin/clubs/${route.params.clubId}/playingAreas/bookings`,
          active: under('/playingAreas/bookings'),
          chip: bookingsNeedSetup.value ? 'Set up' : null,
          hidden: !bookingsFeature.value,
          section: 'club',
        });
        items.push({
          icon: 'mdi-credit-card-outline',
          title: 'Payments',
          link: `/admin/clubs/${route.params.clubId}/payments`,
          active: under('/payments'),
          section: 'club',
        });

        // ── People ──
        items.push({
          icon: 'mdi-account-group',
          title: 'Players',
          link: `/admin/clubs/${route.params.clubId}/members`,
          // A boundary, so /memberships never lights Members up as well.
          active: new RegExp('/admin/clubs/' + clubId + '/members(/|$)').test(route.path),
          section: 'people',
        });
        // Memberships & passes (D31): one feature, the identity icon used nowhere
        // else. The layout drops it while Club.MembershipsEnabled is off.
        items.push({
          icon: 'mdi-card-account-details-outline',
          title: 'Memberships & passes',
          link: `/admin/clubs/${route.params.clubId}/memberships`,
          active: under('/memberships'),
          chip: membershipsNeedSetup.value ? 'Set up' : null,
          badge: !membershipsNeedSetup.value && membershipApplicationsPending.value > 0 ? membershipApplicationsPending.value : null,
          section: 'people',
        });

        // ── Competitions ──
        // X142/X144 (Grant): ONE Competitions page for every format (/competitions, route ClubCompetitions, with
        // format filters; the old per-format list pages redirect there). Lit there and on any competition's own
        // admin pages. Medal for competitions as a whole; the trophy belongs to Tournaments alone.
        // X187 (Grant 2026-10-27): titled "Manage competitions" under the Competitions section (the page's own heading stays).
        items.push({
          icon: 'mdi-medal-outline',
          title: 'Manage competitions',
          link: `/admin/clubs/${route.params.clubId}/competitions`,
          active: under('/competitions') || under('/leagues') || under('/americanos') || under('/ladders') || under('/meets') || under('/tournaments'),
          section: 'competitions',
        });
        items.push({
          icon: 'mdi-scoreboard',
          title: 'Scoring systems',
          link: `/admin/clubs/${route.params.clubId}/scoringSystems`,
          active: under('/scoringSystems'),
          section: 'competitions',
        });

        // ── Outreach ──
        items.push({
          icon: 'mdi-bullhorn',
          title: 'Announcements',
          link: `/admin/clubs/${route.params.clubId}/announcements`,
          active: under('/announcements'),
          section: 'outreach',
        });
        items.push({
          icon: 'mdi-send',
          title: 'Email players',
          link: {
            ...route,
            query: {
              ...route.query,
              mailerQuery: filtersToQuery({
                clubIds: [route.params.clubId]
              })
            }
          },
          active: false,
          section: 'outreach',
        });
        // X142 renamed this "On your website"; X146 (Grant 2026-10-26: "just call it Widgets everywhere") puts Widgets back.
        items.push({
          icon: 'mdi-widgets-outline',
          title: 'Widgets',
          link: `/admin/clubs/${route.params.clubId}/widgets`,
          active: under('/widgets'),
          section: 'outreach',
        });
        items.push({
          icon: 'mdi-television-play',
          title: 'Clubhouse TV',
          link: `/admin/clubs/${route.params.clubId}/displays`,
          active: under('/displays'),
          section: 'outreach',
        });

        // ── Requests & reports ──
        // Requests to follow players this club manages (no account of their own).
        items.push({
          icon: 'mdi-account-heart-outline',
          title: 'Follow requests',
          link: `/admin/clubs/${route.params.clubId}/follow-requests`,
          active: under('/follow-requests'),
          section: 'requests',
        });
        // Online Safety Act: reports members have made about this club's chats.
        items.push({
          icon: 'mdi-flag-outline',
          title: 'Message reports',
          link: `/admin/clubs/${route.params.clubId}/reports`,
          active: under('/reports'),
          section: 'requests',
        });
      }

    }
    return items;
  });

  const adminLowerMenuItems = computed(() => {
    const items = [];
    if(!isSuperUser.value) {
      items.push({
        icon: 'mdi-help-circle-outline',
        title: 'Help',
        link: '/help'
      });
      items.push({
        icon: 'mdi-email-edit-outline',
        title: 'Contact support',
        link: '/contact'
      });
    }
    // Every club admin: the page it opens is also where "Create a club" lives,
    // and a single-club admin has no other way to it. See canSwitchClub.
    if(isSuperUser.value || (isAuthenticated.value && canSwitchClub(user.value))) {
      items.push({
        title: 'Switch club',
        link: '/admin?switchClub=true',
        icon: 'mdi-swap-horizontal'
      });
    }
    items.push({
      title: 'Exit admin',
      link: '/',
      icon: 'mdi-exit-to-app'
    });
    return items;
  })

  const mainMenuItems = computed(() => {
    const items = [];
    items.push(...[
      {title: 'Home', link: '/'},
      {title: 'Clubs', link: '/clubs'},
      {title: 'Sports', link: '/sports'},
      {title: 'Formats', link: '/formats'},
      {title: 'Features', link: '/features'}
    ]);
    items.push(...[
        {title: 'About', link: '/about'},
        {title: 'Help', link: '/help'},
        {title: 'Contact', link: '/contact'},
      ]);
    if (!isAuthenticated.value) {
      items.push({
        title: 'Sign in',
        link: '/login' + stringifyQuery({redirectUrl: route.path}, "?"),
        icon: 'mdi-lock'
      });
      items.push({title: 'Sign up', link: '/register', icon: 'mdi-pencil'});
    }
    return items;
  });
  const userTopRightItems = computed(() => {
    let items = [
      {icon: 'mdi-account', text: 'Your profile', route: 'YourProfile'},
    ];
    items.push({icon: 'mdi-logout', text: 'Sign out', action: 'logout'});
    if (isImpersonating.value) {
      items.push({icon: 'mdi-cancel', text: 'Stop impersonating', action: 'stopImpersonating'});
    }
    return items;
  });
  const onUserTopRightItemClick = async (item) => {
    if (item.link) {
      router.push(item.link).catch(() => {
      });
    } else if (item.action) {
      if (item.action === 'logout') {
        // One sign-out path for the whole site. Going home first no longer
        // works on its own: a required prompt holds that navigation at /prompt.
        await signOutToHome({router, logout});
      } else if (item.action === 'stopImpersonating') {
        await stopImpersonating();
      }
    } else if (item.route) {
      router.push({name: item.route}).catch(() => {
      });
    }
  };
  return {
    convertToLocalRoute,
    adminBreadcrumbs,
    setAdminBreadcrumbs,
    userTopRightItems,
    onUserTopRightItemClick,
    mainMenuItems,
    adminMenuItems,
    adminLowerMenuItems,
    superUserItems
  };
}

